import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';

import { ActivatedRoute, Router } from '@angular/router';

import { NgRedux } from '@angular-redux/store';

import {

  AuthenticationService,

  AuthTrackingService,

  ConfirmModalService,

  ProxyService,

} from '@core/services';

import { LoggingService } from '@core/services/logging.service';

import { ApiRoutes } from '@shared/models/configs/api.endpoints';

import { environment } from '@environments/environment';

import { Actions } from '@core/redux/stores';

import { Subscription } from 'rxjs';



/** Canonical magic-link reason codes (CX API + App Insights). */

type MagicLinkReasonCode =

  | 'magic_link_success'

  | 'magic_link_expired'

  | 'magic_link_consumed'

  | 'magic_link_invalid'

  | 'magic_link_session_conflict'

  | 'magic_link_network_error'

  | 'magic_link_provision_failed'

  | 'magic_link_rate_limited'

  | 'magic_link_redirect_invalid'

  | 'missing_code'

  | 'no_access_token';



interface MagicLinkErrorPresentation {

  header: string;

  message: string;

  reasonCode: MagicLinkReasonCode;

  allowRetry?: boolean;

}

const CUSTOMER_CARE_LINK =
  '<a href="/contact-us">Customer Care</a>';



/**

 * Handles the Auth0 magic-link redirect (Authorization Code flow).

 * OneLink redirect resolves to base .COM with AppLink=magicLink and OAuth query params.

 * state is Base64 JSON from CX. redirectUri in callback POST is OneLink from environment.

 */

@Component({

  selector: 'app-auth0-callback',

  templateUrl: './auth0-callback.component.html',

  styleUrls: ['./auth0-callback.component.less'],

})

export class Auth0CallbackComponent implements OnInit, AfterViewInit, OnDestroy {

  loading = true;

  private pendingCode: string | null = null;

  private callbackHandled = false;

  private subscriptions = new Subscription();



  constructor(

    private router: Router,

    private route: ActivatedRoute,

    private authService: AuthenticationService,

    private authTrackingService: AuthTrackingService,

    private proxyService: ProxyService,

    private confirmModalService: ConfirmModalService,

    private logger: LoggingService,

    private ngRedux: NgRedux<unknown>

  ) {}



  ngOnInit(): void {

    this.subscriptions.add(

      this.confirmModalService.btnEvent.subscribe((action: string) => {

        if (action === 'signOut') {

          this.signOutAndContinue();

        } else if (action === 'staySignedIn' || action === 'close' || action === 'dismiss') {

          this.dismissAndGoHome();

        } else if (action === 'retry') {

          this.retryExchange();

        }

      })

    );

  }



  ngAfterViewInit(): void {

    if (this.callbackHandled) {

      return;

    }

    this.callbackHandled = true;

    this.handleCallback();

  }



  ngOnDestroy(): void {

    this.subscriptions.unsubscribe();

  }



  private signOutAndContinue(): void {

    if (!this.pendingCode) {

      return;

    }

    this.loading = true;

    this.clearClientSession();

    this.exchangeCodeForTokens(this.pendingCode);

  }



  private dismissAndGoHome(): void {

    this.pendingCode = null;

    this.loading = false;

    this.router.navigate(['/home']).catch(() => {});

  }



  /** Clears local session without POST /me/logout (Auth0 refresh tokens are not valid there). */

  private clearClientSession(): void {

    this.ngRedux.dispatch({ type: Actions.USERLOGGEDOUT });

    this.authTrackingService.updateAuth(false, '');

  }



  private retryExchange(): void {

    if (!this.pendingCode) {

      this.dismissAndGoHome();

      return;

    }

    this.exchangeCodeForTokens(this.pendingCode);

  }



  private handleCallback(): void {

    const params = this.getQueryParams();

    const hasCode = !!params.code;

    const hasError = !!params.error;

    const hasState = !!params.state;



    this.trackMagicLinkOutcome('magic_link_callback_started', undefined, {

      hasCode: String(hasCode),

      hasError: String(hasError),

      hasState: String(hasState),

    });



    if (params.error) {

      const presentation = this.mapAuth0RedirectError(

        params.error,

        params.error_description ?? params.error

      );

      this.showErrorModal(presentation);

      return;

    }



    const code = params.code ?? null;

    if (!code) {

      this.showErrorModal({

        header: 'Unable to sign in',

        message: 'Sign-in response was incomplete. Please try again.',

        reasonCode: 'missing_code',

      });

      return;

    }



    if (this.authTrackingService.isLoggedIn()) {

      this.pendingCode = code;

      this.loading = false;

      this.trackMagicLinkOutcome('magic_link_callback_error', 'magic_link_session_conflict');

      const targetEmail = this.parseTargetEmailFromState(params.state ?? null);

      const signedInEmail = this.authTrackingService.getUserEmail()?.trim().toLowerCase();

      const isSameUser =
        !!targetEmail && !!signedInEmail && targetEmail === signedInEmail;

      if (isSameUser) {

        this.confirmModalService.show(

          'You\'re already signed in',

          '',

          'This link was sent to the account you\'re signed in with. You\'re all set and don\'t need to sign out.',

          'Stay Signed In',

          'Sign Out and Use Link',

          '',

          true,

          'staySignedIn',

          'signOut'

        );

      } else {

        const firstName = this.authTrackingService.getFirstName();

        const displayName = firstName ? firstName : 'You';

        const targetDisplay = targetEmail ?? 'a different account';

        this.confirmModalService.show(

          'You\'re signed in as a different account',

          '',

          `This link was sent to ${targetDisplay}. ${displayName} is currently signed in. Sign out to use this link, or stay signed in to continue as ${displayName}.`,

          'Sign Out and Use Link',

          'Stay Signed In',

          '',

          true,

          'signOut',

          'staySignedIn'

        );

      }

      return;

    }



    this.pendingCode = code;

    this.exchangeCodeForTokens(code);

  }



  private exchangeCodeForTokens(code: string): void {

    this.loading = true;

    this.pendingCode = code;



    const params = this.getQueryParams();

    const redirectUri = environment.ciamMagicLinkRedirectUri?.trim();

    if (!redirectUri) {

      this.showErrorModal({

        header: 'Unable to sign in',

        message: 'Magic link sign-in is not configured for this environment.',

        reasonCode: 'magic_link_redirect_invalid',

      });

      return;

    }

    const payload = {

      code,

      state: params.state ?? undefined,

      redirectUri,

    };



    this.proxyService.httpPost(ApiRoutes.AUTH0_MAGICLINK_CALLBACK, payload).subscribe(

      (response: any) => {

        const body = response?.body ?? response;

        if (!body?.accessToken) {

          this.showErrorModal({

            header: 'Unable to sign in',

            message: 'Sign-in succeeded but no access token was returned. Please try again.',

            reasonCode: 'no_access_token',

          });

          return;

        }

        this.authService.setAuth0TokensFromOtpVerify(

          {

            accessToken: body.accessToken,

            refreshToken: body.refreshToken ?? undefined,

            expiresIn: body.expiresIn ?? undefined,

            tokenType: body.tokenType ?? 'Bearer',

          },

          () => {

            this.loading = false;

            this.pendingCode = null;

            this.trackMagicLinkOutcome('magic_link_callback_success', 'magic_link_success');

            this.router.navigate(['/home']).catch(() => {});

          }

        );

      },

      (err) => {

        const presentation = this.mapHttpCallbackFailure(err);

        this.showErrorModal(presentation);

      }

    );

  }



  private showErrorModal(presentation: MagicLinkErrorPresentation): void {

    this.loading = false;

    if (!presentation.allowRetry) {

      this.pendingCode = null;

    }

    this.trackMagicLinkOutcome('magic_link_callback_error', presentation.reasonCode);



    const primaryLabel = presentation.allowRetry ? 'Try Again' : 'OK, Got It';

    const primaryAction = presentation.allowRetry ? 'retry' : 'dismiss';



    this.confirmModalService.show(

      presentation.header,

      '',

      presentation.message,

      primaryLabel,

      '',

      '',

      true,

      primaryAction

    );

  }



  private mapAuth0RedirectError(auth0Error: string, description: string): MagicLinkErrorPresentation {

    const normalizedError = (auth0Error ?? '').trim().toLowerCase();

    const normalizedDescription = (description ?? '').trim().toLowerCase();



    if (this.looksConsumed(normalizedDescription)) {

      return {

        header: 'This link is no longer valid',

        message:

          `This link has already been used. Request a new link from ${CUSTOMER_CARE_LINK} if you still need help signing in.`,

        reasonCode: 'magic_link_consumed',

      };

    }



    if (

      normalizedError === 'unauthorized'

      || normalizedDescription.includes('verification code')

      || normalizedDescription.includes('wrong email')

      || this.looksExpired(normalizedDescription)

    ) {

      return {

        header: 'This link is no longer valid',

        message:

          `This link has expired. Request a new link from ${CUSTOMER_CARE_LINK}, or continue using the site.`,

        reasonCode: 'magic_link_expired',

      };

    }



    return {

      header: 'Unable to sign in',

      message: 'Unable to complete sign-in from this link.',

      reasonCode: 'magic_link_invalid',

    };

  }



  private mapHttpCallbackFailure(err: any): MagicLinkErrorPresentation {

    if (this.isNetworkError(err)) {

      return {

        header: 'Connection problem',

        message: 'We could not reach the server. Check your connection and try again.',

        reasonCode: 'magic_link_network_error',

        allowRetry: true,

      };

    }



    const serverReasonCode = this.normalizeReasonCode(err?.error?.reasonCode);

    const serverMessage =

      typeof err?.error?.message === 'string' && err.error.message.trim().length > 0

        ? err.error.message.trim()

        : undefined;



    if (serverReasonCode) {

      return this.presentationForReasonCode(serverReasonCode, serverMessage);

    }



    if (err?.status === 429) {

      return this.presentationForReasonCode('magic_link_rate_limited', serverMessage);

    }



    return {

      header: 'Unable to sign in',

      message: serverMessage ?? 'Something went wrong while signing you in. Please try again.',

      reasonCode: 'magic_link_invalid',

    };

  }



  private presentationForReasonCode(

    reasonCode: MagicLinkReasonCode,

    serverMessage?: string

  ): MagicLinkErrorPresentation {

    switch (reasonCode) {

      case 'magic_link_expired':

        return {

          header: 'This link is no longer valid',

          message:

            serverMessage

            ?? `This link has expired. Request a new link from ${CUSTOMER_CARE_LINK}, or continue using the site.`,

          reasonCode,

        };

      case 'magic_link_consumed':

        return {

          header: 'This link is no longer valid',

          message:

            serverMessage

            ?? `This link has already been used. Request a new link from ${CUSTOMER_CARE_LINK} if you still need help signing in.`,

          reasonCode,

        };

      case 'magic_link_provision_failed':

        return {

          header: 'Unable to finish setup',

          message:

            serverMessage

            ?? `We could not finish setting up your account. Request a new link from ${CUSTOMER_CARE_LINK}, or continue using the site.`,

          reasonCode,

        };

      case 'magic_link_rate_limited':

        return {

          header: 'Too many attempts',

          message:

            serverMessage

            ?? 'Too many sign-in attempts. Please wait a moment before trying again.',

          reasonCode,

        };

      case 'magic_link_redirect_invalid':

        return {

          header: 'Unable to sign in',

          message:

            serverMessage

            ?? `Unable to complete sign-in from this link. Request a new link from ${CUSTOMER_CARE_LINK}.`,

          reasonCode,

        };

      default:

        return {

          header: 'Unable to sign in',

          message:

            serverMessage

            ?? 'Unable to complete sign-in from this link.',
          reasonCode: 'magic_link_invalid',

        };

    }

  }



  private normalizeReasonCode(value: unknown): MagicLinkReasonCode | undefined {

    if (typeof value !== 'string' || value.trim().length === 0) {

      return undefined;

    }

    const normalized = value.trim() as MagicLinkReasonCode;

    const allowed: MagicLinkReasonCode[] = [

      'magic_link_success',

      'magic_link_expired',

      'magic_link_consumed',

      'magic_link_invalid',

      'magic_link_session_conflict',

      'magic_link_network_error',

      'magic_link_provision_failed',

      'magic_link_rate_limited',

      'magic_link_redirect_invalid',

    ];

    return allowed.includes(normalized) ? normalized : undefined;

  }



  private isNetworkError(err: any): boolean {

    if (!err) {

      return false;

    }

    if (err.status === 0) {

      return true;

    }

    const message = typeof err.message === 'string' ? err.message.toLowerCase() : '';

    return message.includes('network') || message.includes('failed to fetch') || err.name === 'TimeoutError';

  }



  private looksExpired(description: string): boolean {

    return description.includes('expired')

      || description.includes('timeout')

      || description.includes('timed out')

      || description.includes('no longer valid');

  }



  private looksConsumed(description: string): boolean {

    return description.includes('already been used')

      || description.includes('already used')

      || description.includes('invalid verification code')

      || description.includes('wrong email or verification code');

  }



  private trackMagicLinkOutcome(

    eventName: string,

    reasonCode?: MagicLinkReasonCode,

    metadata?: Record<string, string>

  ): void {

    const properties: Record<string, string> = { ...(metadata ?? {}) };

    if (reasonCode) {

      properties.reasonCode = reasonCode;

    }

    this.logger.logEvent(eventName, properties);

  }



  /**
   * CX embeds Base64-encoded JSON in state at send time (targetEmail for session conflict).
   */

  private parseTargetEmailFromState(state: string | null): string | null {

    if (!state) {

      return null;

    }

    try {

      const json = atob(state);

      const parsed = JSON.parse(json);

      if (parsed && typeof parsed.targetEmail === 'string' && parsed.targetEmail.trim().length > 0) {

        return parsed.targetEmail.trim().toLowerCase();

      }

    } catch {

      return null;

    }

    return null;

  }



  /** Use ActivatedRoute (router-parsed) so auth code preserves case; fallback to window.location. */

  private getQueryParams(): Record<string, string> {

    const qp = this.route.snapshot.queryParams;

    if (qp && Object.keys(qp).length > 0) {

      return qp as Record<string, string>;

    }

    const search = typeof window !== 'undefined' ? window.location.search : '';

    if (search && search.startsWith('?')) {

      return this.parseQuery(search.slice(1));

    }

    return {};

  }



  private parseQuery(query: string): Record<string, string> {

    const result: Record<string, string> = {};

    const parts = query.split('&');

    for (const part of parts) {

      const idx = part.indexOf('=');

      if (idx === -1) continue;

      const key = decodeURIComponent(part.slice(0, idx).replace(/\+/g, ' '));

      const value = decodeURIComponent(part.slice(idx + 1).replace(/\+/g, ' '));

      result[key] = value;

    }

    return result;

  }

}


